import { ShieldAlert } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { PageHero, CtaButton, CtaBand, SectionHead, usePageMeta } from "@/components/shared";

const SETUP_SERVICES = [
  "Property assessment", "Vacation-rental readiness", "Amenity recommendations",
  "Furniture & styling recommendations", "Sleeping-capacity planning", "Listing setup",
  "Photography coordination", "Listing copy", "House rules",
  "Pricing strategy", "Cleaning systems", "Linen planning",
  "Guest guide creation", "Check-in system", "Inventory setup", "Launch support",
];

export default function StartAirbnb() {
  usePageMeta("Start Your Airbnb", "Turn your Bermuda property into a guest-ready vacation rental — assessment, setup, photography, listing, pricing and launch support.");
  return (
    <main>
      <PageHero
        overline="Start Your Airbnb"
        lines={["Have a Property?", "Let's Make It Guest-Ready."]}
        copy="You have the home. We build everything else — the assessment, the setup, the listing, the systems and the launch — so your Bermuda property enters the market looking like it has been hosting for years."
        image={IMAGES.beach}
      >
        <CtaButton to="/assessment" variant="accent" testid="start-airbnb-cta-btn">Start My Vacation Rental</CtaButton>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="From Property to Published"
          title="Sixteen steps, handled for you."
          copy="A complete launch service for homeowners who have never operated a vacation rental — and never want to learn the hard way."
        />
        <div className="mt-14 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
          {SETUP_SERVICES.map((item, i) => (
            <Reveal key={item} delay={(i % 4) * 0.05} className="h-full">
              <div className="trace-card flex h-full items-start gap-4 bg-white p-6">
                <span className="font-mono text-[11px] text-bermuda-dark pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm font-medium text-navy/85">{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ocean-light">
        <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionHead
                overline="Bermuda Vacation Rental Readiness"
                title="Registration, handled with you — not for you."
              />
            </div>
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-base leading-relaxed text-navy/75">
                  Vacation rentals in Bermuda are subject to registration and certification requirements administered
                  through the Bermuda Tourism Authority. We assist homeowners with organizing the required information and
                  preparing their property for the registration process — so nothing about your home holds up your launch.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 flex items-start gap-4 border border-navy/10 bg-white p-6">
                  <ShieldAlert size={18} className="mt-0.5 shrink-0 text-bermuda-dark" />
                  <p className="text-sm leading-relaxed text-navy/70">
                    The property owner remains responsible for all legal, regulatory, insurance, tax and ownership
                    requirements. The Bermuda Co-Host provides operational preparation and coordination —{" "}
                    <strong className="text-navy">we do not provide legal advice.</strong>
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Ready to see what your property could earn?" copy="Start with an honest property assessment — potential, preparation needs and a launch plan in writing." />
    </main>
  );
}
